import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import FabAnunciar from "../components/FabAnunciar";
import { useLocation as useAJLocation } from "../lib/location";
import { Hammer, Lightning, TreeEvergreen, SprayBottle, Wrench, Television, ArrowRight, CaretRight, X, Funnel } from "@phosphor-icons/react";
import useFocusRefresh from "../lib/useFocusRefresh";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";

const MAIN_ICONS = {
  "Construção Civil": Hammer,
  "Ferramentas Elétricas": Lightning,
  "Eletrônicos": Television,
  "Jardinagem e Paisagismo": TreeEvergreen,
  "Limpeza e Lavagem": SprayBottle,
};
const MAIN_COLORS = {
  "Construção Civil": "#004E98",
  "Ferramentas Elétricas": "#FF6B35",
  "Eletrônicos": "#6366F1",
  "Jardinagem e Paisagismo": "#16A34A",
  "Limpeza e Lavagem": "#0EA5E9",
};

export default function Home() {
  const [cats, setCats] = useState({ main: [], all: [] });
  // V18.1 — Hidrata do cache ANTES do fetch, pra evitar "sem anúncios" ao voltar/abrir
  const HOME_CACHE_KEY = "aj_home_featured_v1";
  const [featured, setFeatured] = useState(() => {
    try {
      const raw = sessionStorage.getItem(HOME_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.data) ? parsed.data : [];
    } catch { return []; }
  });
  const [featuredLoading, setFeaturedLoading] = useState(() => {
    try { return !sessionStorage.getItem(HOME_CACHE_KEY); } catch { return true; }
  });
  const [showAll, setShowAll] = useState(false);
  const { location } = useAJLocation();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  // Reload featured when city changes
  const loadFeatured = useCallback(() => {
    const params = { limit: 12 };
    if (location?.city) params.city = location.city;
    if (location?.lat) params.user_lat = location.lat;
    if (location?.lng) params.user_lng = location.lng;
    // Só mostra "loading" quando ainda não tem nada em tela (evita blink)
    setFeaturedLoading((prev) => prev && (featured.length === 0));
    return api.get("/listings", { params })
      .then((r) => {
        setFeatured(r.data || []);
        // V18.1 — salva no cache pra próxima abertura hidratar instantâneo
        try { sessionStorage.setItem(HOME_CACHE_KEY, JSON.stringify({ data: r.data || [], ts: Date.now() })); } catch { /* ignore */ }
      })
      // V18.1 — em erro, MANTÉM os anúncios que já estavam em tela (não zera)
      .catch(() => { /* preserva estado atual */ })
      .finally(() => setFeaturedLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.city, location?.lat, location?.lng]);

  useEffect(() => { loadFeatured(); }, [loadFeatured]);

  // V7.1: Refresh on focus + pull-to-refresh
  useFocusRefresh(loadFeatured, [location?.city]);
  const ptr = usePullToRefresh(loadFeatured);

  return (
    <div data-testid="home-page" className="pb-6">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      {/* HERO - simplified */}
      <section className="bg-gradient-to-br from-[#004E98] to-[#003A73] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
          <h1 className="font-heading text-3xl md:text-5xl font-black leading-[1.05]">
            Alugue ferramentas <br/>
            <span className="text-[#FF6B35]">do seu vizinho.</span>
          </h1>
          {location?.city && (
            <p className="mt-3 text-sm text-white/80" data-testid="home-city-label">
              Mostrando itens em <b className="text-white">{location.city} - {location.uf}</b>
            </p>
          )}
        </div>
      </section>

      {/* MAIN CATEGORIES + See all */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-lg">Categorias</h2>
          <button
            onClick={() => setShowAll(true)}
            className="text-sm font-semibold text-[#004E98] inline-flex items-center gap-1"
            data-testid="see-all-categories"
          >
            Ver todas <CaretRight size={14} weight="bold" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {cats.main.map((key) => {
            const Icon = MAIN_ICONS[key] || Wrench;
            const color = MAIN_COLORS[key] || "#004E98";
            return (
              <Link
                key={key}
                to={`/buscar?category=${encodeURIComponent(key)}`}
                className="aj-card flex flex-col items-center justify-center p-3 gap-2 text-center"
                data-testid={`category-${key}`}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${color}15` }}>
                  <Icon size={24} weight="duotone" color={color} />
                </div>
                <span className="text-[11px] font-semibold leading-tight line-clamp-2 min-h-[2.4em]">{key}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-heading font-bold text-xl">Em destaque</h2>
          <Link to="/buscar" className="text-sm font-semibold text-[#004E98]" data-testid="see-all-listings">Ver tudo</Link>
        </div>
        {featuredLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-featured-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aj-card overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-200" />
                <div className="p-2.5 space-y-2">
                  <div className="h-3 bg-slate-200 rounded w-3/4" />
                  <div className="h-3 bg-slate-200 rounded w-1/2" />
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="aj-card p-10 text-center" data-testid="home-featured-empty">
            <Wrench size={36} weight="duotone" className="mx-auto text-slate-300" />
            <p className="text-slate-500 mt-3">Ainda não há anúncios. Seja o primeiro!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {featured.map((it) => <ListingCard key={it.id} item={it} />)}
          </div>
        )}
        {featured.length > 0 && (
          <div className="mt-6 text-center">
            <Link
              to="/buscar"
              data-testid="home-ver-mais"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#004E98] text-white font-bold text-sm hover:bg-[#003d7a] transition-colors"
            >
              Ver mais itens <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        )}
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 mt-10">
        <h2 className="font-heading font-bold text-xl mb-4">Como funciona</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { n: "1", t: "Encontre", d: "Busque na sua região por categoria ou nome." },
            { n: "2", t: "Reserve", d: "Pague com segurança. O dono aceita em até 1h." },
            { n: "3", t: "Token & Use", d: "Confirme a entrega com o token de 4 dígitos." },
          ].map((s) => (
            <div key={s.n} className="aj-card p-4">
              <div className="w-9 h-9 rounded-full bg-[#FF6B35] text-white font-bold flex items-center justify-center">{s.n}</div>
              <div className="font-heading font-bold text-lg mt-2">{s.t}</div>
              <p className="text-sm text-slate-500 mt-0.5">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* All categories overlay */}
      {showAll && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setShowAll(false)} data-testid="all-categories-overlay">
          <div className="bg-white text-[#0F172A] w-full md:max-w-lg md:rounded-2xl rounded-t-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-xl">Todas as categorias</h3>
              <button onClick={() => setShowAll(false)} data-testid="close-all-categories"><X size={22} weight="bold" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {cats.all.map((c) => (
                <button
                  key={c}
                  onClick={() => { nav(`/buscar?category=${encodeURIComponent(c)}`); setShowAll(false); }}
                  className="text-left p-3 rounded-xl border border-slate-200 hover:bg-slate-50 font-semibold text-sm"
                  data-testid={`all-category-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <FabAnunciar />
    </div>
  );
}
